import { Link } from 'react-router-dom';
import type { SubmissionSummary } from '../gallery/galleryApi';

/**
 * Status pill that shows the most recent submission for a project
 * (matched by name + kind). Hidden when there's no submission.
 *
 * The status string from the API is machine-readable
 * (`awaiting_parent_consent`); we render a kid-friendly label for
 * display purposes but keep the raw string in `aria-label` for
 * screen readers and tests.
 *
 * Approved submissions render as a link to `/gallery` so the kid can
 * jump from "your work was approved" to seeing it live next to other
 * kids' work. Every other status is informational, so the pill stays
 * a static `<span>`.
 */
const STATUS_LABELS: Record<string, string> = {
  pending: 'pending',
  processing: 'reviewing',
  awaiting_parent_consent: 'waiting on grown-up',
  awaiting_teacher_review: 'waiting for teacher',
  awaiting_moderator: 'waiting for review',
  approved: 'approved',
  rejected: 'rejected',
  withdrawn: 'withdrawn',
};

export interface PublishStatusBadgeProps {
  submissions: SubmissionSummary[];
  projectName: string;
  projectKind: 'image' | 'audio' | 'pose';
}

export function PublishStatusBadge({
  submissions,
  projectName,
  projectKind,
}: PublishStatusBadgeProps): JSX.Element | null {
  const matching = submissions
    .filter((s) => s.project_name === projectName && s.project_kind === projectKind)
    .sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime());
  const latest = matching[0];
  if (!latest) return null;
  const label = STATUS_LABELS[latest.status] ?? latest.status;
  if (latest.status === 'approved') {
    return (
      <Link
        to="/gallery"
        className="bl-project__pub-badge bl-project__pub-badge--approved bl-project__pub-badge--link"
        title="See your project in the public gallery"
        aria-label="Submission status: approved (open the gallery)"
      >
        {label} ↗
      </Link>
    );
  }
  return (
    <span
      className={`bl-project__pub-badge bl-project__pub-badge--${latest.status}`}
      title={latest.rejection_reason ?? `Submission ${latest.status}`}
      aria-label={`Submission status: ${latest.status}`}
    >
      {label}
    </span>
  );
}
